<template>
  <div class="page">
    <div class="page-header">
      <div>
        <h1 class="page-title">Cobranças</h1>
        <p class="page-help">Quem já pagou, quem está devendo e quanto falta entrar.</p>
      </div>
    </div>

    <SemEventoBanner v-if="!eventos.length && !carregandoEventos" />

    <div v-else class="filtros card">
      <div class="filtro-row">
        <label class="label">Evento</label>
        <select class="select" v-model="eventoId" @change="carregar()">
          <option v-for="e in eventos" :key="e.id" :value="e.id">{{ e.nome }}</option>
        </select>
      </div>
      <div class="filtro-row">
        <label class="label">Buscar</label>
        <input class="input" v-model="busca" placeholder="Nome do inscrito" @keyup.enter="carregar()" />
      </div>
    </div>

    <div v-if="eventoId" class="tabs card">
      <button v-for="s in SITUACOES" :key="s.valor" class="tab"
              :class="{ active: situacao === s.valor }"
              @click="situacao = s.valor; carregar()">
        {{ s.rotulo }}
      </button>
    </div>

    <!-- Totalizadores: somam exatamente as linhas exibidas abaixo -->
    <div v-if="totais && !loading" class="totais">
      <div class="total-card card">
        <span class="total-label">Arrecadado</span>
        <strong class="total-valor valor-ok">{{ formatMoney(totais.arrecadado) }}</strong>
      </div>
      <div class="total-card card">
        <span class="total-label">A receber</span>
        <strong class="total-valor">{{ formatMoney(totais.aReceber) }}</strong>
      </div>
      <div class="total-card card">
        <span class="total-label">Em atraso</span>
        <strong class="total-valor" :class="totais.emAtraso > 0 ? 'valor-alerta' : ''">
          {{ formatMoney(totais.emAtraso) }}
        </strong>
      </div>
      <div class="total-card card">
        <span class="total-label">Inscritos</span>
        <strong class="total-valor">
          {{ totais.inscritos }}
          <small v-if="totais.atrasados">· {{ totais.atrasados }} atrasado(s)</small>
        </strong>
      </div>
    </div>

    <SkeletonLoader v-if="loading" />
    <EmptyState v-else-if="!cobrancas.length" icon="📋" title="Nada por aqui"
                :description="situacao || busca ? 'Nenhum inscrito com esse filtro.' : 'Esse evento ainda não tem cobranças.'" />

    <div v-else class="lista">
      <div v-for="c in cobrancas" :key="c.grupoId" class="cobranca card" :class="`sit-${c.situacao}`">
        <div class="cob-topo">
          <div class="cob-quem">
            <strong class="cob-nome">{{ c.nome }}</strong>
            <span v-if="c.tamanho > 1" class="cob-grupo">grupo de {{ c.tamanho }}</span>
            <span class="cob-plano">{{ c.plano }}</span>
          </div>
          <span class="chip" :class="`chip-${c.situacao}`">{{ rotuloSituacao(c.situacao) }}</span>
        </div>

        <!-- A régua: um quadradinho por parcela. Mostra ritmo e buraco de uma
             olhada, do jeito que um carnê de papel mostra. -->
        <div v-if="c.carne" class="regua" :aria-label="`${c.pagas} de ${c.total} parcelas pagas`">
          <button v-for="p in c.parcelas" :key="p.id"
                  type="button"
                  class="parcela"
                  :class="[`p-${p.situacao}`, { 'p-manual': p.manual, clicavel: podeLancar && p.situacao !== 'paga' }]"
                  :title="tituloParcela(p)"
                  :disabled="!podeLancar || p.situacao === 'paga'"
                  @click="abrirLancamento(c, p)">
            <span class="parcela-num">{{ p.numero }}</span>
          </button>
        </div>

        <div class="cob-baixo">
          <span class="cob-contagem"><strong>{{ c.pagas }}/{{ c.total }}</strong> pagas</span>
          <span class="cob-sep">·</span>
          <span>{{ formatMoney(c.valorPago) }} pagos</span>
          <template v-if="c.valorEmAberto > 0">
            <span class="cob-sep">·</span>
            <span>{{ formatMoney(c.valorEmAberto) }} em aberto</span>
          </template>
          <template v-if="c.situacao === 'atrasado'">
            <span class="cob-sep">·</span>
            <span class="cob-atraso">atrasada desde {{ formatDate(primeiraVencida(c)) }}</span>
          </template>
          <template v-else-if="c.proximoVencimento">
            <span class="cob-sep">·</span>
            <span>vence {{ formatDate(c.proximoVencimento) }}</span>
          </template>
        </div>

        <!-- Quem lançou cada pagamento por fora fica visível: sem isso,
             "por que essa parcela está paga se não tem cobrança?" vira arqueologia. -->
        <div v-if="manuais(c).length" class="cob-manuais">
          <div v-for="p in manuais(c)" :key="`m-${p.id}`" class="manual-linha">
            <span class="manual-tag">Parcela {{ p.numero }} recebida por fora</span>
            <span v-if="p.registradoPor" class="manual-quem">lançada por {{ p.registradoPor }}</span>
            <span v-if="p.observacao" class="manual-obs">“{{ p.observacao }}”</span>
          </div>
        </div>

        <div v-if="podeLancar && temLancavel(c)" class="cob-acoes">
          <button class="btn-sm btn-secondary" @click="abrirLancamento(c)">Registrar pagamento por fora</button>
        </div>
      </div>
    </div>

    <!-- Lançamento manual -->
    <div v-if="lancando" class="modal-overlay" @click.self="fecharLancamento()">
      <div class="modal-card">
        <h2 class="modal-title">Registrar pagamento por fora</h2>
        <p class="modal-sub">{{ lancando.nome }} · {{ lancando.plano }}</p>

        <div class="aviso-manual">
          Este lançamento não passa por gateway nenhum: ele vale pela sua palavra.
          Fica gravado quem registrou, quando e por quê.
        </div>

        <div class="form-row">
          <label class="label">Quais parcelas foram pagas?</label>
          <div class="escolha-parcelas">
            <label v-for="p in lancaveis(lancando)" :key="p.id" class="escolha"
                   :class="{ active: selecionadas.includes(p.id) }">
              <input type="checkbox" :value="p.id" v-model="selecionadas" />
              <span class="escolha-num">Parcela {{ p.numero }}</span>
              <span class="escolha-venc">vence {{ formatDate(p.vencimento) }}</span>
              <span class="escolha-valor">{{ formatMoney(p.valor) }}</span>
            </label>
          </div>
          <p v-if="!lancaveis(lancando).length" class="help-text">
            Nenhuma parcela desta inscrição aceita lançamento manual.
          </p>
        </div>

        <div v-if="selecionadas.length" class="total-lancamento">
          Total a registrar: <strong>{{ formatMoney(totalSelecionado) }}</strong>
          <small v-if="selecionadas.length > 1">({{ selecionadas.length }} parcelas)</small>
        </div>

        <div class="form-row">
          <label class="label">Como o dinheiro foi recebido? *</label>
          <textarea class="input" rows="2" v-model="observacao" maxlength="500"
                    placeholder="Ex.: PIX direto na chave da igreja em 12/10, comprovante com a tesouraria"></textarea>
          <span class="help-text">Obrigatório. É o que responde “por que essa parcela está paga?” daqui a seis meses.</span>
        </div>

        <div v-if="erro" class="error-banner">{{ erro }}</div>

        <div class="modal-actions">
          <button class="btn-secondary" @click="fecharLancamento()">Cancelar</button>
          <button class="btn-primary" :disabled="!podeSalvar || salvando" @click="salvarLancamento()">
            {{ salvando ? 'Registrando...' : 'Registrar recebimento' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import {
  getCobrancas, registrarPagamentoManual, SITUACOES, formatMoney, formatDate
} from '../../lib/cobrancas'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'

const ROTULOS = { 'em-dia': 'Em dia', atrasado: 'Atrasado', quitado: 'Quitado' }

export default {
  name: 'CobrancasPage',
  components: { SkeletonLoader, EmptyState, SemEventoBanner },
  data() {
    return {
      eventoId: '',
      situacao: '',
      busca: '',
      cobrancas: [],
      totais: null,
      podeLancar: false,
      loading: false,
      carregandoEventos: true,
      lancando: null,
      selecionadas: [],
      observacao: '',
      salvando: false,
      erro: '',
      SITUACOES
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    eventos() {
      return this.eventosStore.eventos.filter(e => e.status !== 'ARQUIVADO')
    },
    totalSelecionado() {
      if (!this.lancando) return 0
      return this.lancando.parcelas
        .filter(p => this.selecionadas.includes(p.id))
        .reduce((s, p) => s + p.valor, 0)
    },
    podeSalvar() {
      return this.selecionadas.length > 0 && this.observacao.trim().length >= 3
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    this.carregandoEventos = false
    if (this.eventos.length) {
      this.eventoId = this.eventos[0].id
      await this.carregar()
    }
  },
  methods: {
    formatMoney,
    formatDate,
    rotuloSituacao(s) { return ROTULOS[s] || s },
    async carregar() {
      if (!this.eventoId) return
      this.loading = true
      try {
        const d = await getCobrancas(this.eventoId, { situacao: this.situacao, q: this.busca.trim() })
        this.cobrancas = d.cobrancas
        this.totais = d.totais
        this.podeLancar = d.podeLancar
      } catch (err) {
        this.$toast?.open({
          message: err.response?.data?.message || 'Falha ao carregar cobranças',
          type: 'error', position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },
    /** Parcelas que aceitam lançamento: nem pagas, nem já cobradas no gateway. */
    lancaveis(c) {
      return c ? c.parcelas.filter(p => p.situacao === 'vencida' || p.situacao === 'a-vencer') : []
    },
    temLancavel(c) { return this.lancaveis(c).length > 0 },
    manuais(c) { return c.parcelas.filter(p => p.manual) },
    primeiraVencida(c) {
      const v = c.parcelas.filter(p => p.situacao === 'vencida')
      return v.length ? v[0].vencimento : null
    },
    tituloParcela(p) {
      const base = `Parcela ${p.numero} · ${formatMoney(p.valor)} · vence ${formatDate(p.vencimento)}`
      if (p.situacao === 'paga') {
        return p.manual
          ? `${base} · recebida por fora${p.registradoPor ? ` (${p.registradoPor})` : ''}`
          : `${base} · paga`
      }
      return p.situacao === 'vencida' ? `${base} · VENCIDA` : base
    },
    abrirLancamento(c, parcela = null) {
      if (!this.podeLancar) return
      this.lancando = c
      this.selecionadas = parcela && parcela.situacao !== 'paga' ? [parcela.id] : []
      this.observacao = ''
      this.erro = ''
    },
    fecharLancamento() {
      this.lancando = null
      this.selecionadas = []
      this.observacao = ''
      this.erro = ''
    },
    async salvarLancamento() {
      this.salvando = true
      this.erro = ''
      try {
        const r = await registrarPagamentoManual({
          pagamentoIds: this.selecionadas,
          observacao: this.observacao.trim()
        })
        this.$toast?.open({ message: r.message, type: 'success', position: 'top-right' })
        this.fecharLancamento()
        await this.carregar()
      } catch (err) {
        this.erro = err.response?.data?.message || 'Falha ao registrar o pagamento.'
      } finally {
        this.salvando = false
      }
    }
  }
}
</script>

<style scoped>
.filtros { display: grid; grid-template-columns: 2fr 1fr; gap: 12px; padding: 14px; }
.filtro-row { display: flex; flex-direction: column; gap: 6px; }

.totais { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px; }
.total-card { padding: 14px; display: flex; flex-direction: column; gap: 4px; }
.total-label { font-size: var(--text-xs); color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.5px; }
.total-valor { font-size: var(--text-lg); font-weight: 700; }
.total-valor small { font-size: var(--text-xs); font-weight: 500; color: var(--color-text-secondary); }
.valor-ok { color: #166534; }
.valor-alerta { color: #b91c1c; }

.lista { display: flex; flex-direction: column; gap: 12px; }
.cobranca { padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.cob-topo { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
.cob-quem { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.cob-nome { font-size: var(--text-base); }
.cob-grupo, .cob-plano { font-size: var(--text-xs); color: var(--color-text-secondary); }

/* A régua. `flex-wrap` em vez de largura fixa: um carnê de 9 tem que caber no
   celular sem espremer o quadradinho até virar risco. */
.regua { display: flex; flex-wrap: wrap; gap: 5px; }
.parcela {
  width: 34px; height: 30px; border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  border: 1px solid transparent; padding: 0;
  font-family: inherit; cursor: default;
}
.parcela.clicavel { cursor: pointer; }
.parcela.clicavel:hover { outline: 2px solid var(--color-primary, #1d4ed8); outline-offset: 1px; }
.p-paga { background: #dcfce7; color: #166534; }
.p-vencida { background: #fee2e2; color: #b91c1c; border-color: #fca5a5; }
.p-a-vencer { background: #f3f4f6; color: #6b7280; }
.p-cancelada { background: #fff; color: #d1d5db; border-color: #e5e7eb; text-decoration: line-through; }
/* Recebida por fora continua verde — para "está em dia?" a origem não importa.
   O tracinho embaixo responde outra pergunta: de onde veio esse dinheiro. */
.p-manual { box-shadow: inset 0 -3px 0 #92400e; }

.cob-baixo { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; font-size: var(--text-sm); color: var(--color-text-secondary); }
.cob-sep { opacity: 0.5; }
.cob-atraso { color: #b91c1c; font-weight: 600; }

.cob-manuais { display: flex; flex-direction: column; gap: 4px; border-top: 1px dashed var(--color-border, #e5e7eb); padding-top: 8px; }
.manual-linha { display: flex; flex-wrap: wrap; gap: 6px; font-size: var(--text-xs); }
.manual-tag { font-weight: 600; color: #92400e; }
.manual-quem { color: var(--color-text-secondary); }
.manual-obs { color: var(--color-text-muted); font-style: italic; }

.cob-acoes { display: flex; justify-content: flex-end; }

.chip { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 3px 8px; border-radius: 4px; white-space: nowrap; }
.chip-quitado { background: #dcfce7; color: #166534; }
.chip-atrasado { background: #fee2e2; color: #b91c1c; }
.chip-em-dia { background: #dbeafe; color: #1d4ed8; }

.modal-sub { margin: 0 0 12px; color: var(--color-text-secondary); font-size: var(--text-sm); }
.aviso-manual {
  background: #fef3c7; color: #92400e; border-radius: 6px;
  padding: 10px 12px; font-size: var(--text-sm); margin-bottom: 14px; line-height: 1.4;
}
.escolha-parcelas { display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow-y: auto; }
.escolha {
  display: flex; align-items: center; gap: 10px; padding: 9px 11px;
  border: 1px solid var(--color-border, #e5e7eb); border-radius: 6px; cursor: pointer;
}
.escolha.active { border-color: var(--color-primary, #1d4ed8); background: #eff6ff; }
.escolha-num { font-weight: 600; font-size: var(--text-sm); }
.escolha-venc { font-size: var(--text-xs); color: var(--color-text-secondary); flex: 1; }
.escolha-valor { font-weight: 700; font-size: var(--text-sm); }
.total-lancamento { padding: 10px 0; font-size: var(--text-sm); }

@media (max-width: 900px) {
  .totais { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .filtros { grid-template-columns: 1fr; }
  .cob-topo { flex-direction: column; }
  .parcela { width: 30px; height: 28px; }
  .escolha { flex-wrap: wrap; }
  .escolha-venc { flex-basis: 100%; }
}
</style>
